import { cn, type TableColumn, Typography } from "@kyan/ui";

import { getOptionConstraints } from "#/config/orders";
import { tooltips } from "#/config/tooltips";
import { MarketPair } from "#/types/markets";
import { type OptionChainEntry } from "#/types/options";
import { getStrikePrecision } from "#/utils/getStrikePrecision";
import { getOptionInstrumentProperties } from "#/utils/instrument.utils";
import { getUnderlyingSymbolFromMarketPair } from "#/utils/marketPair";
import {
  formatCurrency,
  formatPercent,
  formatValue,
} from "#/utils/valueFormatters";

import { type Columns } from "./components/ColumnsDropdown";
import type { OptionChainColumnGroup } from "./OptionChain.types";

export const strikeColumn: TableColumn<
  OptionChainEntry,
  OptionChainColumnGroup
> = {
  id: "strike",
  header: "Strike",
  minWidth: "80px",
  cell: ({ strike, dist }) => (
    <div className="flex h-12 flex-col items-center justify-center">
      <Typography bold grotesk primary size={14}>
        {formatValue(strike, { precision: getStrikePrecision(strike) })}
      </Typography>
      {dist !== null && (
        <Typography
          size={10}
          className={cn({
            "text-success": dist >= 0,
            "text-error": dist < 0,
          })}
        >
          {formatPercent(dist, { precision: 0 })}
        </Typography>
      )}
    </div>
  ),
  cellClassName: "justify-center border-r lg:border-l border-divider py-0",
  columnClassName: "justify-center border-r lg:border-l border-divider",
  tooltip: tooltips.strike,
};

export const callColumns: TableColumn<
  OptionChainEntry,
  OptionChainColumnGroup
>[] = [
  {
    id: "callOpen",
    header: "OI",
    group: "C:bid",
    cell: (row) => (row.callOpen ? formatValue(row.callOpen) : "-"),
    tooltip: tooltips.callOpen,
  },
  {
    id: "callRho",
    header: "ρ Rho",
    group: "C:bid",
    tooltip: tooltips.callRho,
  },
  {
    id: "callTheta",
    header: "Θ Theta",
    group: "C:bid",
    tooltip: tooltips.callTheta,
  },
  {
    id: "callVega",
    header: "V Vega",
    group: "C:bid",
    tooltip: tooltips.callVega,
  },
  {
    id: "callGamma",
    header: "Γ Gamma",
    group: "C:bid",
    tooltip: tooltips.callGamma,
  },
  {
    id: "callDelta",
    header: "∆ Delta",
    group: "C:bid",
    tooltip: tooltips.callDelta,
  },
  {
    id: "callBidSize",
    header: "Size",
    group: "C:bid",
    tooltip: tooltips.callBidSize,
    cell: (row) => formatValue(row.callBidSize),
  },
  {
    id: "callIVBid",
    header: "IV bid",
    group: "C:bid",
    tooltip: tooltips.callIVBid,
    cell: (row) =>
      Number(row?.callIVBid) > 0
        ? formatPercent(row.callIVBid, { precision: 1, signDisplay: "never" })
        : "-",
  },
  {
    id: "callBid",
    header: "Bid",
    group: "C:bid",
    cell: (row) => {
      const pair = row?.callInstrumentName?.split("-")[0] as MarketPair;
      const market = getUnderlyingSymbolFromMarketPair(pair);
      const decimals =
        getOptionConstraints(market)?.priceDisplayPrecisionNotional ?? 0;
      return (
        <Typography className="text-success">
          {formatCurrency(row.callBid, { precision: decimals })}
        </Typography>
      );
    },
    cellClassName: "pr-2",
    columnClassName: "pr-2",
    tooltip: tooltips.callBid,
  },
  {
    id: "callMark",
    header: "Mark",
    tooltip: tooltips.callMark,
    cell: (row) => {
      const { underlying } = getOptionInstrumentProperties(
        row.callInstrumentName,
      );
      const { priceDisplayPrecisionNotional: priceDecimals } =
        getOptionConstraints(underlying);
      return (
        <Typography>
          {formatCurrency(row.callMark, { precision: priceDecimals })}
        </Typography>
      );
    },
  },
  {
    id: "callAsk",
    header: "Ask",
    group: "C:ask",
    cell: (row) => {
      const pair = row?.callInstrumentName?.split("-")[0] as MarketPair;
      const market = getUnderlyingSymbolFromMarketPair(pair);
      const decimals =
        getOptionConstraints(market)?.priceDisplayPrecisionNotional ?? 0;
      return (
        <Typography className="text-error">
          {formatCurrency(row.callAsk, { precision: decimals })}
        </Typography>
      );
    },
    tooltip: tooltips.callAsk,
  },
  {
    id: "callIVAsk",
    header: "IV ask",
    group: "C:ask",
    tooltip: tooltips.callIVAsk,
    cell: (row) =>
      Number(row?.callIVAsk) > 0
        ? formatPercent(row.callIVAsk, { precision: 1, signDisplay: "never" })
        : "-",
  },
  {
    id: "callAskSize",
    header: "Size",
    group: "C:ask",
    tooltip: tooltips.callAskSize,
    cell: (row) => formatValue(row.callAskSize),
  },
  {
    id: "callPosition",
    header: "Position",
    group: "C:ask",
    cellClassName: "pr-2",
    columnClassName: "pr-2",
    tooltip: tooltips.callPosition,
  },
];

export const putColumns: TableColumn<
  OptionChainEntry,
  OptionChainColumnGroup
>[] = [
  {
    id: "putPosition",
    header: "Position",
    group: "P:bid",
    cellClassName: "pl-2",
    columnClassName: "pl-2",
    tooltip: tooltips.putPosition,
  },
  {
    id: "putBidSize",
    header: "Size",
    group: "P:bid",
    tooltip: tooltips.putBidSize,
    cell: (row) => formatValue(row.putBidSize),
  },
  {
    id: "putIVBid",
    header: "IV bid",
    group: "P:bid",
    tooltip: tooltips.putIVBid,
    cell: (row) =>
      Number(row?.putIVBid) > 0
        ? formatPercent(row.putIVBid, { precision: 1, signDisplay: "never" })
        : "-",
  },
  {
    id: "putBid",
    header: "Bid",
    group: "P:bid",
    cell: (row) => {
      const pair = row?.putInstrumentName?.split("-")[0] as MarketPair;
      const market = getUnderlyingSymbolFromMarketPair(pair);
      const decimals =
        getOptionConstraints(market)?.priceDisplayPrecisionNotional ?? 0;
      return (
        <Typography className="text-success">
          {formatCurrency(row.putBid, { precision: decimals })}
        </Typography>
      );
    },
    tooltip: tooltips.putBid,
  },
  {
    id: "putMark",
    header: "Mark",
    tooltip: tooltips.putMark,
    cell: (row) => {
      const { underlying } = getOptionInstrumentProperties(
        row.putInstrumentName,
      );
      const { priceDisplayPrecisionNotional: priceDecimals } =
        getOptionConstraints(underlying);
      return (
        <Typography>
          {formatCurrency(row.putMark, { precision: priceDecimals })}
        </Typography>
      );
    },
  },
  {
    id: "putAsk",
    header: "Ask",
    group: "P:ask",
    cell: (row) => {
      const pair = row?.putInstrumentName?.split("-")[0] as MarketPair;
      const market = getUnderlyingSymbolFromMarketPair(pair);
      const decimals =
        getOptionConstraints(market)?.priceDisplayPrecisionNotional ?? 0;
      return (
        <Typography className="text-error">
          {formatCurrency(row.putAsk, { precision: decimals })}
        </Typography>
      );
    },
    cellClassName: "pl-2",
    columnClassName: "pl-2",
    tooltip: tooltips.putAsk,
  },
  {
    id: "putIVAsk",
    header: "IV ask",
    group: "P:ask",
    tooltip: tooltips.putIVAsk,
    cell: (row) =>
      Number(row?.putIVAsk) > 0
        ? formatPercent(row.putIVAsk, { precision: 1, signDisplay: "never" })
        : "-",
  },
  {
    id: "putAskSize",
    header: "Size",
    group: "P:ask",
    tooltip: tooltips.putAskSize,
    cell: (row) => formatValue(row.putAskSize),
  },
  {
    id: "putDelta",
    header: "∆ Delta",
    group: "P:ask",
    tooltip: tooltips.putDelta,
  },
  {
    id: "putGamma",
    header: "Γ Gamma",
    group: "P:ask",
    tooltip: tooltips.putGamma,
  },
  {
    id: "putVega",
    header: "V Vega",
    group: "P:ask",
    tooltip: tooltips.putVega,
  },
  {
    id: "putTheta",
    header: "Θ Theta",
    group: "P:ask",
    tooltip: tooltips.putTheta,
  },
  {
    id: "putRho",
    header: "ρ Rho",
    group: "P:ask",
    tooltip: tooltips.putRho,
  },
  {
    id: "putOpen",
    header: "OI",
    group: "P:ask",
    cell: (row) => (row.putOpen ? formatValue(row.putOpen) : "-"),
    tooltip: tooltips.putOpen,
  },
];

export const desktopDefaultShownColumns: Columns = {
  open: true,
  rho: false,
  theta: false,
  vega: false,
  gamma: false,
  delta: true,
  bidSize: true,
  ivBid: true,
  bid: true,
  mark: true,
  ask: true,
  ivAsk: true,
  askSize: true,
  position: true,
};

export const mobileDefaultShownColumns: Columns = {
  open: false,
  rho: false,
  theta: false,
  vega: false,
  gamma: false,
  delta: true,
  bidSize: true,
  ivBid: true,
  bid: true,
  mark: true,
  ask: true,
  ivAsk: true,
  askSize: true,
  position: false,
};

export const fallbackOptionchainData: OptionChainEntry[] = Array.from(
  { length: 10 },
  () => ({
    callInstrumentName: "",
    callOpen: 0,
    callVolume: 0,
    callRho: 0,
    callTheta: 0,
    callVega: 0,
    callGamma: 0,
    callDelta: 0,
    callBidSize: 0,
    callIVBid: 0,
    callBid: 0,
    callMark: 0,
    callAsk: 0,
    callIVAsk: 0,
    callAskSize: 0,
    callPosition: 0,
    strike: 0,
    dist: null,
    putPosition: 0,
    putAskSize: 0,
    putIVAsk: 0,
    putAsk: 0,
    putMark: 0,
    putBid: 0,
    putIVBid: 0,
    putBidSize: 0,
    putDelta: 0,
    putGamma: 0,
    putVega: 0,
    putTheta: 0,
    putRho: 0,
    putVolume: 0,
    putOpen: 0,
    putInstrumentName: "",
  }),
);
